"use client";
import Link from "next/link";
import { motion } from "framer-motion";
import { pageVariants } from "@/lib/animations";
import type { AgreementListItem, Agreement } from "@/lib/server/agreements";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import { prepareAgreementHtml } from "@/lib/normalize-agreement-html";
import CookiePolicyTable from "@/components/cookie-consent/CookiePolicyTable";

const COOKIE_POLICY_SLUGS = ["cookie-policy-and-cookie-notice", "cookie-policy", "cerez-politikasi"];

export interface ContractsPageProps {
    currentSlug?: string;
    contractsPageUrl?: string;
    language?: string;
    defaultLanguage?: string;
    agreements: AgreementListItem[];
    currentAgreement: Agreement | null;
    translations?: Record<string, string>;
}

interface AgreementLink {
    title: string;
    link: string;
    active: boolean;
}

function renderAgreementContent(html: string): string {
    return sanitizeCmsHtml(prepareAgreementHtml(html));
}

export default function ContractsPage({
    currentSlug,
    contractsPageUrl,
    language: propLanguage,
    defaultLanguage: propDefaultLanguage,
    agreements,
    currentAgreement,
    translations: serverTranslations,
}: ContractsPageProps) {
    const { getVariable } = useLanguageVariables();
    const contractsUrl = contractsPageUrl;
    const isTurkish = (propLanguage ?? propDefaultLanguage ?? "en").startsWith("tr");

    const label = (key: string, tr: string, en: string) =>
        serverTranslations?.[key] ?? getVariable(key, isTurkish ? tr : en);

    const links: AgreementLink[] = agreements.map((agreement) => {
        const agreementUrl = `${contractsUrl}/${agreement.slug}`.replace(/\/+/g, "/");
        return {
            title: agreement.title,
            link: agreementUrl,
            active: currentSlug === agreement.slug,
        };
    });

    return (
        <motion.div
            className="w-9/12 mx-auto px-4 py-16"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            <div className="grid grid-cols-12 gap-8 my-[120px]">
                <div className="col-span-12 lg:col-span-3 print:hidden">
                    <ul className="w-full bg-[#F4F6F8] px-6 py-10">
                        {links.map((subItem, subIndex) => (
                            <li
                                key={subItem.link}
                                className={`group mb-3 border-l-3 transition-all ease-in-out duration-300 hover:bg-white hover:shadow-link hover:border-alsero-first hover:rounded-lg h-[70px] ${
                                    subItem.active
                                        ? "bg-white shadow-link border-alsero-first rounded-lg"
                                        : "border-transparent"
                                }`}
                            >
                                <Link
                                    href={subItem.link}
                                    className="w-full h-full ps-6 flex items-center text-lg font-medium text-[#0C0C11]/60 group-hover:text-[#0C0C11]"
                                >
                                    {subItem.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="col-span-12 lg:col-span-9 lg:ps-6 print:col-span-12">
                    <div className="px-6 lg:px-0" id="agreement-print-area">
                        {currentAgreement ? (
                            <>
                                <div className="flex flex-col gap-4 border-b border-[#0C0C11]/10 pb-5 mb-8 sm:flex-row sm:items-center sm:justify-between">
                                    <h1 className="text-3xl font-bold text-[#0C0C11] text-balance">
                                        {currentAgreement.title}
                                    </h1>
                                    <div className="flex items-center gap-2 print:hidden shrink-0">
                                        <a
                                            href={`/api/agreements/${encodeURIComponent(currentAgreement.slug)}/pdf?language=${encodeURIComponent(propLanguage ?? "")}&download=1`}
                                            className="inline-flex items-center gap-2 rounded-lg border border-alsero-first/30 bg-alsero-first/5 px-4 py-2 text-sm font-semibold text-alsero-first transition-colors hover:bg-alsero-first hover:text-white"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                            {label("contracts_download_pdf", "PDF İndir", "Download PDF")}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => window.print()}
                                            className="inline-flex items-center gap-2 rounded-lg border border-[#0C0C11]/15 px-4 py-2 text-sm font-semibold text-[#0C0C11]/70 transition-colors hover:bg-[#0C0C11]/5 hover:text-[#0C0C11]"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                                            {label("contracts_print", "Yazdır", "Print")}
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-8">
                                    <div
                                        className="agreement-content"
                                        dangerouslySetInnerHTML={{
                                            __html: renderAgreementContent(currentAgreement.content),
                                        }}
                                    />
                                    {COOKIE_POLICY_SLUGS.includes(currentAgreement.slug) && (
                                        <CookiePolicyTable />
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <div className="text-gray-500">
                                    {label("contracts_not_found", "Sözleşme bulunamadı", "Contract not found")}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
